import React, { useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import { Box, Text, Spinner, useBreakpointValue } from "@chakra-ui/react";
import { addCountries } from "../../../reducer";
import { checkErrorMessage } from "../../../tools/tools";
import Details from "./details";
import ServiceActionBar from "./ServiceActionBar";
import { StyledCard, StyledCardContent } from "../../../stats/sharedCardStyles";

/**
 * Toggles a boolean flag on a service via API PUT request.
 * Updates the service in the parent list optimistically.
 * @param {object} service - Service object to update
 * @param {string} flagName - Flag name ("test" or "hidden")
 * @param {string} endpoint - API endpoint suffix (e.g., "switch_test")
 * @param {function} setLoading - Loading state setter
 * @param {function} updateServiceInList - Parent callback to update the service
 * @param {string} successMessage - Toast message on success
 */
async function toggleServiceFlag(service, flagName, endpoint, setLoading, updateServiceInList, successMessage) {
    setLoading(true);
    try {
        await axios.put(`/admin/services/${endpoint}/${service?.id.toString()}`);
        const updatedService = { ...service, [flagName]: !service[flagName] };
        updateServiceInList(updatedService);
        toast.success(successMessage);
    } catch (error) {
        const errorMessage = checkErrorMessage(error)?.message;
        toast.error(errorMessage || "Error while updating service status");
    } finally {
        setLoading(false);
    }
}

/**
 * Resolves the country data for a service from the local cache or API.
 * Dispatches the result to the Redux store for future lookups.
 * @param {object} service - Service with a country field
 * @param {Array} countries - Already known countries from Redux
 * @param {function} setServiceCountry - State setter for resolved country
 * @param {function} dispatch - Redux dispatch function
 */
async function resolveCountry(service, countries, setServiceCountry, dispatch) {
    const country = service?.country;
    const cached = countries?.find(tmp =>
        country?.toLowerCase() === tmp.name.toLowerCase() ||
        country?.toLowerCase() === tmp.nativeName.toLowerCase()
    );

    if (!cached) {
        const { data } = await axios.get(`flags/search/country/${country}`);
        setServiceCountry(data);
        await dispatch(addCountries([data, ...countries]));
    } else {
        setServiceCountry(cached);
    }
}

/**
 * Placeholder card shown when no service data is available.
 * @param {object} props
 * @param {boolean} props.isMobile - Whether the viewport is mobile-sized
 */
function EmptyServiceCard({ isMobile }) {
    return (
        <StyledCard>
            <StyledCardContent display="flex" alignItems="center" justifyContent="center" padding="40px">
                <Text color="rgba(255, 255, 255, 0.6)"
                    fontSize={isMobile ? "0.85rem" : "0.95rem"} textAlign="center">
                    Aucun service trouvé
                </Text>
            </StyledCardContent>
        </StyledCard>
    );
}

/**
 * Builds the toggle test handler for the current service.
 * @param {object} service - Service object
 * @param {function} setLoadingTest - Loading state setter for test toggle
 * @param {function} updateServiceInList - Parent callback to update the service
 */
function handleToggleTest(service, setLoadingTest, updateServiceInList) {
    const msg = `Mode ${!service.test ? "Test" : "Production"} activé`;
    toggleServiceFlag(service, "test", "switch_test", setLoadingTest, updateServiceInList, msg);
}

/**
 * Builds the toggle hidden handler for the current service.
 * @param {object} service - Service object
 * @param {function} setLoadingHidden - Loading state setter for hidden toggle
 * @param {function} updateServiceInList - Parent callback to update the service
 */
function handleToggleHidden(service, setLoadingHidden, updateServiceInList) {
    const msg = `Fiche ${!service.hidden ? "masquée" : "visible"}`;
    toggleServiceFlag(service, "hidden", "switch_hidden", setLoadingHidden, updateServiceInList, msg);
}

/**
 * Main service component displaying actions bar, contact dialog, and service details.
 * Handles test mode toggling, visibility toggling, and country resolution.
 * @param {object} props
 * @param {object} props.service - Service data object
 * @param {function} props.updateServiceInList - Callback to update the service in the parent list
 */
function Service({ service, updateServiceInList }) {
    const dispatch = useDispatch();
    const countries = useSelector(state => state.global.countries);
    const [serviceCountry, setServiceCountry] = useState(undefined);
    const [loadingTest, setLoadingTest] = useState(false);
    const [loadingHidden, setLoadingHidden] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const currency = serviceCountry?.currencies?.symbol || serviceCountry;
    const isMobile = useBreakpointValue({ base: true, sm: false });

    useEffect(() => {
        if (service?.country && !serviceCountry) {
            setIsLoading(true);
            resolveCountry(service, countries, setServiceCountry, dispatch)
                .then(() => setIsLoading(false));
        }
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [serviceCountry, service?.country]);

    if (!service?.id) {
        return <EmptyServiceCard isMobile={isMobile} />;
    }

    return (
        <div>
            <ServiceContent
                service={service}
                isMobile={isMobile}
                loadingTest={loadingTest}
                loadingHidden={loadingHidden}
                currency={currency}
                serviceCountry={serviceCountry}
                isLoading={isLoading}
                onToggleTest={() => handleToggleTest(service, setLoadingTest, updateServiceInList)}
                onToggleHidden={() => handleToggleHidden(service, setLoadingHidden, updateServiceInList)}
            />
        </div>
    );
}

/**
 * Renders the service action bar and details content area.
 * @param {object} props
 * @param {object} props.service - Service data object
 * @param {boolean} props.isMobile - Whether the viewport is mobile-sized
 * @param {boolean} props.loadingTest - Whether test toggle is processing
 * @param {boolean} props.loadingHidden - Whether visibility toggle is processing
 * @param {string|object} props.currency - Resolved currency symbol
 * @param {object} props.serviceCountry - Resolved country data
 * @param {boolean} props.isLoading - Whether the country data is still loading
 * @param {function} props.onToggleTest - Handler for test mode toggle
 * @param {function} props.onToggleHidden - Handler for visibility toggle
 */
function ServiceContent({ service, isMobile, loadingTest, loadingHidden, currency, serviceCountry, isLoading, onToggleTest, onToggleHidden }) {
    return (
        <div>
            <ServiceActionBar
                service={service}
                isMobile={isMobile}
                loadingTest={loadingTest}
                loadingHidden={loadingHidden}
                onToggleTest={onToggleTest}
                onToggleHidden={onToggleHidden}
            />
            <Box flexGrow={1} overflowY="auto" maxHeight="calc(100vh - 80px)">
                {isLoading ? (
                    <Box display="flex" justifyContent="center" py={4}>
                        <Spinner color="rgba(255, 255, 255, 0.7)" />
                    </Box>
                ) : (
                    <Details service={service} currency={currency} serviceCountry={serviceCountry} />
                )}
            </Box>
        </div>
    );
}

export default Service;
